import React from 'react';
import { Typography, Card, Layout, Button, Row, Col } from 'antd';
import { 
  ArrowLeftOutlined, 
  LockOutlined,
  SecurityScanOutlined,
  AuditOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph } = Typography;
const { Content } = Layout;

const Security = () => {
  const navigate = useNavigate();

  const securityFeatures = [
    {
      icon: <LockOutlined style={{ fontSize: 32, color: '#52c41a' }} />,
      title: 'Data Protection',
      description: 'Implementation of industry-standard data protection measures for secure information handling.'
    },
    {
      icon: <SecurityScanOutlined style={{ fontSize: 32, color: '#1890ff' }} />,
      title: 'Access Control',
      description: 'Multi-layered access control systems to ensure authorized access to platform resources.'
    },
    {
      icon: <AuditOutlined style={{ fontSize: 32, color: '#722ed1' }} />,
      title: 'Security Monitoring',
      description: 'Continuous monitoring and assessment of security systems and protocols.'
    },
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: 32, color: '#fa8c16' }} />,
      title: 'Compliance Standards',
      description: 'Adherence to industry compliance standards and security best practices.'
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #FAFBFE 0%, #F4F6FB 30%, #EEF2FA 100%)' }}>
      <Content style={{ padding: '24px 48px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/')}
            style={{ marginBottom: 16 }}
          >
            Back to Home
          </Button>
        </div>

        <Card style={{ boxShadow: '0 4px 16px rgba(139, 122, 174, 0.12)', borderRadius: 16, marginBottom: 24 }}>
          <Title level={1} style={{ color: '#2D3748', marginBottom: 32 }}>Security & Trust</Title>
          
          <Paragraph style={{ fontSize: '18px', color: '#4A5568', marginBottom: 32 }}>
            At Vouchain, security is our top priority. We implement enterprise-grade security measures to protect your 
            business data, transactions, and voucher codes. Our comprehensive security framework ensures the highest 
            level of protection for your B2B operations.
          </Paragraph>

          <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
            {securityFeatures.map((feature, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <Card
                  style={{ 
                    textAlign: 'center', 
                    height: '100%',
                    border: '1px solid rgba(139, 122, 174, 0.15)',
                    borderRadius: 12
                  }}
                  bodyStyle={{ padding: 20 }}
                >
                  <div style={{ marginBottom: 16 }}>{feature.icon}</div>
                  <Title level={4} style={{ color: '#2D3748', marginBottom: 12 }}>{feature.title}</Title>
                  <Paragraph style={{ color: '#4A5568', fontSize: '14px', margin: 0 }}>
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>

        <Card style={{ boxShadow: '0 4px 16px rgba(139, 122, 174, 0.12)', borderRadius: 16 }}>
          <Title level={2} style={{ color: '#2D3748' }}>Security Overview</Title>
          
          <Paragraph style={{ color: '#4A5568' }}>
            Our platform implements multiple layers of security measures to protect your business data and transactions. 
            We continuously monitor and update our security protocols to maintain the highest standards of protection.
          </Paragraph>

          <Title level={3} style={{ color: '#2D3748' }}>Key Security Areas</Title>
          <ul style={{ color: '#4A5568', marginLeft: 16 }}>
            <li>Infrastructure and network security</li>
            <li>Data protection and encryption</li>
            <li>Access control and authentication</li>
            <li>Secure voucher code management</li>
            <li>Continuous monitoring and threat detection</li>
          </ul>

          <Title level={2} style={{ color: '#2D3748' }}>Security Best Practices for Users</Title>
          <Paragraph style={{ color: '#4A5568' }}>
            <strong>Account Security:</strong>
          </Paragraph>
          <ul style={{ color: '#4A5568', marginLeft: 16 }}>
            <li>Enable multi-factor authentication on your account</li>
            <li>Use strong, unique passwords and update them regularly</li>
            <li>Never share your account credentials with unauthorized personnel</li>
            <li>Regularly review account activity and report suspicious behavior</li>
            <li>Keep your contact information up to date for security notifications</li>
          </ul>

          <Paragraph style={{ color: '#4A5568' }}>
            <strong>Voucher Code Handling:</strong>
          </Paragraph>
          <ul style={{ color: '#4A5568', marginLeft: 16 }}>
            <li>Store voucher codes securely and limit access to authorized personnel</li>
            <li>Do not share voucher codes through unsecured communication channels</li>
            <li>Monitor voucher code usage and report any unauthorized redemptions</li>
            <li>Implement internal controls for voucher code distribution</li>
          </ul>

          <Title level={2} style={{ color: '#2D3748' }}>Incident Response</Title>
          <Paragraph style={{ color: '#4A5568' }}>
            Our 24/7 Security Operations Center (SOC) monitors for security threats and incidents. In the event of a 
            security incident:
          </Paragraph>
          <ul style={{ color: '#4A5568', marginLeft: 16 }}>
            <li>Immediate containment and assessment of the incident</li>
            <li>Notification to affected customers within 24 hours</li>
            <li>Detailed investigation and remediation planning</li>
            <li>Post-incident review and security improvements</li>
            <li>Compliance with regulatory reporting requirements</li>
          </ul>

          <Title level={2} style={{ color: '#2D3748' }}>Security Contact</Title>
          <Paragraph style={{ color: '#4A5568' }}>
            For security-related inquiries, vulnerability reports, or incident notifications, please contact us through our platform.
          </Paragraph>

          <Title level={2} style={{ color: '#2D3748' }}>Transparency Reports</Title>
          <Paragraph style={{ color: '#4A5568' }}>
            We periodically publish reports detailing our security posture and compliance status. These reports are available to customers through the platform.
          </Paragraph>
        </Card>
      </Content>
    </Layout>
  );
};

export default Security; 